/**
 * An in-process game session, exposed through the same `Transport` interface a
 * WebSocket client will implement in Phase 10.
 *
 * This is the most architecturally load-bearing small file in the project. The
 * client talks to a `Transport`: it sends intents and receives server messages.
 * It has no idea whether the thing on the other end is a simulation running in
 * the same JavaScript context or a server across the Atlantic, and it must never
 * find out. When multiplayer arrives, a WebSocket client implements this same
 * interface and everything above it is untouched.
 *
 * Nothing here is throwaway. The local transport stays the fastest way to test
 * game logic forever — no network, no lobby, no second process.
 */
import { generateBotIntents } from "../bots/simpleBot.js";
import { applyIntent } from "../game/intents.js";
import { buildSessionEndedMessage, checkSessionEnd, createSession, endSession, kingdomIdForIndex, } from "../game/session.js";
import { stepSimulation } from "../game/simulation.js";
import { eventAffectedKeys, toHexState, toKingdomState, toMapState } from "../game/state.js";
import { GameLoop, TICK_RATE_HZ } from "../tick.js";
export class LocalSession {
    state;
    humanKingdomId;
    loop;
    handlers = new Set();
    endedAnnounced = false;
    constructor(options = {}) {
        this.state = createSession(options);
        const humanIndex = options.humanKingdomIndex === undefined ? 0 : options.humanKingdomIndex;
        this.humanKingdomId = humanIndex === null ? null : kingdomIdForIndex(humanIndex);
        this.loop = new GameLoop({
            onTick: ({ tick, dtMs, intents }) => {
                this.runTick(tick, dtMs, intents);
            },
        });
        // Seed the first tick's worth of bot orders so the AI is already moving when
        // the player's first frame renders, rather than standing still for 100ms.
        this.submitBotIntents();
    }
    get tick() {
        return this.state.tick;
    }
    get running() {
        return this.loop.running;
    }
    /**
     * Connect a client. Emits `fullState` immediately, exactly as a real server
     * does on join, so the client has one code path for "here is the world".
     */
    connect() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias -- the returned
        // object is a plain Transport, not a class, so it needs the session captured.
        const session = this;
        return {
            sendIntent(intent) {
                if (session.humanKingdomId === null)
                    return;
                // Straight into the tick queue — the same queue the bots use, and the
                // same one a WebSocket handler will push into. No shortcut, no direct
                // mutation, no "it's local so it's fine" special case.
                session.loop.submitIntent(session.humanKingdomId, intent);
            },
            onMessage(handler) {
                session.handlers.add(handler);
                handler(session.buildFullState());
                return () => {
                    session.handlers.delete(handler);
                };
            },
            disconnect() {
                session.stop();
            },
        };
    }
    start() {
        this.loop.start();
    }
    stop() {
        this.loop.stop();
    }
    /** One tick, ignoring real time. For tests and fast-forwarding. */
    stepOnce() {
        this.loop.stepOnce();
    }
    runTick(tick, dtMs, intents) {
        if (this.state.ended)
            return;
        // The session owns the tick counter; stepSimulation only reads it, to stamp
        // eliminations. Setting it first means an elimination and the intents that
        // preceded it agree on which tick they happened.
        this.state.tick = tick;
        for (const queued of intents) {
            // Rejections are ordinary play — a bot asking for a hex it can no longer
            // afford is not an error worth surfacing from the transport layer.
            applyIntent(this.state, queued.kingdomId, queued.intent);
        }
        const landed = stepSimulation(this.state, dtMs / 1000);
        // Announced before the delta so a client sees "an eruption started" ahead of
        // the hexes it changed, rather than having to infer the cause from effects.
        for (const event of landed) {
            this.broadcast({
                type: "worldEvent",
                tick: this.state.tick,
                event: event.event,
                affectedHexes: event.affectedHexes,
            });
        }
        const result = checkSessionEnd(this.state);
        if (result !== null) {
            endSession(this.state, result);
            this.broadcastDelta();
            if (!this.endedAnnounced) {
                this.endedAnnounced = true;
                this.broadcast(buildSessionEndedMessage(this.state, result));
            }
            this.loop.stop();
            return;
        }
        this.broadcastDelta();
        // Queue the bots' next move for the *following* tick, not this one, so AI
        // kingdoms carry the same one-tick latency a network client does. Letting
        // them act inside the tick they observe would make them quietly faster than
        // any human could ever be, and the game would feel unfair for a reason the
        // player could never see.
        this.submitBotIntents();
    }
    submitBotIntents() {
        for (const kingdom of this.state.kingdoms.values()) {
            if (!kingdom.isBot || kingdom.eliminated)
                continue;
            for (const intent of generateBotIntents(this.state, kingdom.id)) {
                if (!this.loop.submitIntent(kingdom.id, intent))
                    break;
            }
        }
    }
    buildFullState() {
        return {
            type: "fullState",
            tick: this.state.tick,
            yourKingdomId: this.humanKingdomId ?? kingdomIdForIndex(0),
            map: toMapState(this.state),
            kingdoms: [...this.state.kingdoms.values()].map(toKingdomState),
        };
    }
    /**
     * Broadcast only what changed. docs/02 permits full-state broadcast for the
     * prototype, but `state.dirtyHexes` already exists, so deltas cost nothing
     * here — and mean Phase 10 has one less thing to retrofit under real load.
     */
    broadcastDelta() {
        if (this.handlers.size === 0) {
            this.state.dirtyHexes.clear();
            return;
        }
        const affected = eventAffectedKeys(this.state);
        const changed = [];
        for (const key of this.state.dirtyHexes) {
            const record = this.state.hexes.get(key);
            if (record !== undefined) {
                changed.push(toHexState(record, this.state.tick, affected.has(key)));
            }
        }
        this.state.dirtyHexes.clear();
        this.broadcast({
            type: "stateDelta",
            tick: this.state.tick,
            changedHexes: changed,
            kingdoms: [...this.state.kingdoms.values()].map(toKingdomState),
            activeEvents: this.state.activeEvents.map((active) => ({
                id: active.id,
                event: active.event,
                startedAtTick: active.startedAtTick,
                endsAtTick: active.endsAtTick,
                affectedHexes: [...active.hexKeys]
                    .map((key) => this.state.hexes.get(key)?.coord)
                    .filter((coord) => coord !== undefined),
            })),
        });
    }
    broadcast(message) {
        for (const handler of this.handlers)
            handler(message);
    }
}
export { TICK_RATE_HZ };
//# sourceMappingURL=localSession.js.map